import React from 'react';
import { FaGavel, FaHandshake, FaExclamationTriangle, FaCreditCard, FaUserCheck, FaBalanceScale } from 'react-icons/fa';

const TermsOfServicePage = () => {
  return (
    <div className="terms-of-service-page">
      <div className="terms-container">
        <div className="terms-header">
          <FaGavel className="terms-icon" />
          <h1>Terms of Service</h1>
          <p className="last-updated">Last updated: December 22, 2025</p>
          <p className="intro">
            Welcome to BroaderBusiness. These Terms of Service ("Terms") govern your use of our AI-powered 
            marketing intelligence platform and all related services. By using BroaderBusiness, you agree 
            to be bound by these Terms.
          </p>
        </div>

        <div className="terms-content">
          {/* Acceptance of Terms */}
          <section className="terms-section">
            <div className="section-header">
              <FaHandshake className="section-icon" />
              <h2>1. Acceptance of Terms</h2>
            </div>
            
            <div className="subsection">
              <p>By accessing or using BroaderBusiness ("Service," "Platform," or "we"), you agree to:</p>
              <ul>
                <li>Be bound by these Terms and our Privacy Policy</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Use the Service only for lawful business purposes</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
              
              <p>If you do not agree to these Terms, you may not use our Service.</p>
            </div>
          </section>

          {/* Description of Service */}
          <section className="terms-section">
            <div className="section-header">
              <FaUserCheck className="section-icon" />
              <h2>2. Description of Service</h2>
            </div>
            
            <div className="subsection">
              <h3>2.1 Platform Features</h3>
              <p>BroaderBusiness provides a comprehensive marketing intelligence platform that includes:</p>
              <ul>
                <li><strong>AI Campaign Creation:</strong> Artificial intelligence-powered campaign development and optimization</li>
                <li><strong>Social Media Management:</strong> Integration with Instagram, Facebook, X (Twitter), and Google My Business</li>
                <li><strong>POS Integration:</strong> Connection to Toast, Shopify, Square, and other point-of-sale systems</li>
                <li><strong>Local Market Analysis:</strong> Competitor research and local business intelligence</li>
                <li><strong>Analytics Dashboard:</strong> Performance tracking and business insights</li>
                <li><strong>Content Generation:</strong> AI-assisted marketing content creation</li>
                <li><strong>Campaign Scheduling:</strong> Automated posting and campaign management</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>2.2 Service Availability</h3>
              <ul>
                <li>We strive for 99.9% uptime but cannot guarantee uninterrupted service</li>
                <li>Planned maintenance will be announced in advance when possible</li>
                <li>Third-party service outages may affect certain features</li>
                <li>Emergency maintenance may occur without prior notice for security reasons</li>
              </ul>
            </div>
          </section>

          {/* User Accounts and Eligibility */}
          <section className="terms-section">
            <div className="section-header">
              <FaUserCheck className="section-icon" />
              <h2>3. User Accounts and Eligibility</h2>
            </div>
            
            <div className="subsection">
              <h3>3.1 Account Requirements</h3>
              <ul>
                <li><strong>Age Requirement:</strong> You must be at least 18 years old and legally able to enter into contracts</li>
                <li><strong>Business Use:</strong> Service is intended for legitimate business purposes only</li>
                <li><strong>Accurate Information:</strong> All registration information must be current, complete, and accurate</li>
                <li><strong>Account Security:</strong> You are responsible for maintaining the security of your login credentials</li>
                <li><strong>One Account:</strong> Each user may maintain only one active account</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>3.2 Account Responsibilities</h3>
              <ul>
                <li>Keep your password secure and confidential</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Update your contact information promptly</li>
                <li>Monitor your account for suspicious activity</li>
                <li>Accept responsibility for all account activity</li>
              </ul>
            </div>
          </section>

          {/* Acceptable Use Policy */}
          <section className="terms-section">
            <div className="section-header">
              <FaExclamationTriangle className="section-icon" />
              <h2>4. Acceptable Use Policy</h2>
            </div>
            
            <div className="subsection">
              <h3>4.1 Permitted Uses</h3>
              <ul>
                <li>Create and manage legitimate marketing campaigns for your business</li>
                <li>Connect and manage your social media and POS system accounts</li>
                <li>Generate business insights and analytics</li>
                <li>Use AI tools for content creation and optimization</li>
                <li>Research local market conditions and competitors</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>4.2 Prohibited Uses</h3>
              <p>You agree NOT to use the Service to:</p>
              <ul>
                <li><strong>Illegal Activities:</strong> Violate any laws, regulations, or third-party rights</li>
                <li><strong>Spam and Abuse:</strong> Send unsolicited messages, spam, or engage in harassment</li>
                <li><strong>Harmful Content:</strong> Create, distribute, or promote harmful, offensive, or inappropriate content</li>
                <li><strong>Intellectual Property Violations:</strong> Infringe on copyrights, trademarks, or other intellectual property</li>
                <li><strong>Security Breaches:</strong> Attempt to hack, compromise, or disrupt our systems</li>
                <li><strong>Unauthorized Access:</strong> Access accounts, data, or systems without permission</li>
                <li><strong>Competitive Intelligence:</strong> Use our Service to build competing products or services</li>
                <li><strong>False Information:</strong> Provide false, misleading, or deceptive information</li>
                <li><strong>Adult Content:</strong> Create or distribute adult or sexually explicit content</li>
                <li><strong>Hate Speech:</strong> Promote discrimination, hatred, or violence</li>
                <li><strong>Automated Abuse:</strong> Use bots or automated tools to abuse the Service</li>
                <li><strong>Data Mining:</strong> Scrape or extract data without permission</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>4.3 Social Media Platform Compliance</h3>
              <p>When using our social media features, you must also comply with:</p>
              <ul>
                <li>Instagram's Terms of Use and Community Guidelines</li>
                <li>Facebook's Terms of Service and Community Standards</li>
                <li>X (Twitter)'s Terms of Service and Rules</li>
                <li>Google My Business Terms of Service</li>
                <li>All applicable platform-specific policies and guidelines</li>
              </ul>
            </div>
          </section>

          {/* Content and Intellectual Property */}
          <section className="terms-section">
            <div className="section-header">
              <FaBalanceScale className="section-icon" />
              <h2>5. Content and Intellectual Property</h2>
            </div>
            
            <div className="subsection">
              <h3>5.1 Your Content</h3>
              <ul>
                <li><strong>Ownership:</strong> You retain ownership of all content you create or upload</li>
                <li><strong>License to Us:</strong> You grant us a limited license to use, store, and process your content to provide our services</li>
                <li><strong>Responsibility:</strong> You are solely responsible for your content and its legal compliance</li>
                <li><strong>Backup:</strong> You should maintain backups of important content</li>
                <li><strong>Removal:</strong> You may delete your content at any time through your account</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>5.2 AI-Generated Content</h3>
              <ul>
                <li><strong>Ownership:</strong> You own AI-generated content created through our platform</li>
                <li><strong>Review Required:</strong> You must review and approve all AI-generated content before publication</li>
                <li><strong>Accuracy:</strong> AI-generated content may not always be accurate or appropriate</li>
                <li><strong>Compliance:</strong> Ensure AI-generated content complies with all applicable laws and platform policies</li>
                <li><strong>Training Data:</strong> Anonymized AI interactions may be used to improve our algorithms</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>5.3 Our Intellectual Property</h3>
              <ul>
                <li><strong>Platform Rights:</strong> We own all rights to the BroaderBusiness platform, code, and algorithms</li>
                <li><strong>Trademarks:</strong> BroaderBusiness and our logos are our registered trademarks</li>
                <li><strong>Limited License:</strong> We grant you a limited, non-exclusive license to use our platform</li>
                <li><strong>Restrictions:</strong> You may not copy, modify, or reverse engineer our platform</li>
              </ul>
            </div>
          </section>

          {/* Third-Party Integrations */}
          <section className="terms-section">
            <div className="section-header">
              <FaHandshake className="section-icon" />
              <h2>6. Third-Party Integrations</h2>
            </div>
            
            <div className="subsection">
              <h3>6.1 Social Media Platforms</h3>
              <ul>
                <li><strong>Authorization:</strong> You authorize us to access your connected social media accounts</li>
                <li><strong>Platform Changes:</strong> Third-party platform changes may affect functionality</li>
                <li><strong>Data Access:</strong> We only access data necessary for our services</li>
                <li><strong>Revocation:</strong> You may revoke platform access at any time</li>
                <li><strong>Platform Compliance:</strong> Your use must comply with each platform's terms</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>6.2 POS System Integrations</h3>
              <ul>
                <li><strong>Data Accuracy:</strong> We rely on POS systems for accurate sales and inventory data</li>
                <li><strong>Real-time Sync:</strong> Data synchronization depends on third-party API availability</li>
                <li><strong>Security:</strong> All POS integrations use secure, encrypted connections</li>
                <li><strong>Permissions:</strong> You must have authority to connect business POS systems</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>6.3 Third-Party Limitations</h3>
              <ul>
                <li>We are not responsible for third-party service outages or changes</li>
                <li>Third-party terms and privacy policies apply to their services</li>
                <li>We may need to modify integrations based on third-party requirements</li>
                <li>Some features may become unavailable due to third-party restrictions</li>
              </ul>
            </div>
          </section>

          {/* Payment Terms */}
          <section className="terms-section">
            <div className="section-header">
              <FaCreditCard className="section-icon" />
              <h2>7. Payment Terms</h2>
            </div>
            
            <div className="subsection">
              <h3>7.1 Subscription Plans</h3>
              <ul>
                <li><strong>Plan Types:</strong> We offer various subscription plans with different features and limits</li>
                <li><strong>Billing Cycle:</strong> Subscriptions are billed monthly or annually as selected</li>
                <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless canceled</li>
                <li><strong>Plan Changes:</strong> You may upgrade or downgrade your plan at any time</li>
                <li><strong>Prorated Billing:</strong> Plan changes are prorated to your billing cycle</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>7.2 Payment Processing</h3>
              <ul>
                <li><strong>Payment Methods:</strong> We accept major credit cards and PayPal</li>
                <li><strong>Secure Processing:</strong> All payments are processed securely by certified payment processors</li>
                <li><strong>Failed Payments:</strong> Service may be suspended for failed payments</li>
                <li><strong>Currency:</strong> All prices are in US Dollars unless otherwise specified</li>
                <li><strong>Taxes:</strong> You are responsible for any applicable taxes</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>7.3 Refunds and Cancellations</h3>
              <ul>
                <li><strong>Cancellation:</strong> You may cancel your subscription at any time</li>
                <li><strong>No Partial Refunds:</strong> No refunds for partial month usage</li>
                <li><strong>Annual Plans:</strong> Annual subscriptions may be eligible for prorated refunds within 30 days</li>
                <li><strong>Service Continuation:</strong> Service continues until the end of your paid period after cancellation</li>
                <li><strong>Data Retention:</strong> Account data is retained for 30 days after cancellation</li>
              </ul>
            </div>
          </section>

          {/* Privacy and Data Protection */}
          <section className="terms-section">
            <div className="section-header">
              <FaUserCheck className="section-icon" />
              <h2>8. Privacy and Data Protection</h2>
            </div>
            
            <div className="subsection">
              <h3>8.1 Data Collection and Use</h3>
              <ul>
                <li>Our Privacy Policy details how we collect, use, and protect your data</li>
                <li>We only collect data necessary to provide our services</li>
                <li>Your data is encrypted and stored securely</li>
                <li>We do not sell your personal data to third parties</li>
                <li>You maintain control over your data and privacy settings</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>8.2 AI and Machine Learning</h3>
              <ul>
                <li>We use AI to improve our services and provide recommendations</li>
                <li>Anonymized data may be used to train our algorithms</li>
                <li>You can opt out of certain AI features if desired</li>
                <li>AI-generated insights are based on available data and may not be perfect</li>
              </ul>
            </div>
          </section>

          {/* Service Modifications and Availability */}
          <section className="terms-section">
            <div className="section-header">
              <FaExclamationTriangle className="section-icon" />
              <h2>9. Service Modifications and Availability</h2>
            </div>
            
            <div className="subsection">
              <h3>9.1 Service Changes</h3>
              <ul>
                <li>We may modify, update, or discontinue features with reasonable notice</li>
                <li>New features may be added to improve the service</li>
                <li>Price changes require 30 days advance notice</li>
                <li>Major changes to terms require user acceptance</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>9.2 Service Availability</h3>
              <ul>
                <li>We aim for high availability but cannot guarantee 100% uptime</li>
                <li>Maintenance windows may cause temporary service interruptions</li>
                <li>Third-party outages may affect integrated services</li>
                <li>We provide status updates during significant outages</li>
              </ul>
            </div>
          </section>

          {/* Disclaimers and Limitations */}
          <section className="terms-section">
            <div className="section-header">
              <FaExclamationTriangle className="section-icon" />
              <h2>10. Disclaimers and Limitations</h2>
            </div>
            
            <div className="subsection">
              <h3>10.1 Service Disclaimers</h3>
              <ul>
                <li><strong>"AS IS" Service:</strong> The service is provided "as is" without warranties</li>
                <li><strong>No Guarantees:</strong> We do not guarantee specific marketing results or outcomes</li>
                <li><strong>AI Limitations:</strong> AI recommendations are suggestions and may not always be accurate</li>
                <li><strong>Third-Party Reliability:</strong> We are not responsible for third-party service failures</li>
                <li><strong>Data Accuracy:</strong> While we strive for accuracy, data may contain errors</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>10.2 Limitation of Liability</h3>
              <ul>
                <li>Our liability is limited to the amount you paid in the past 12 months</li>
                <li>We are not liable for indirect, incidental, or consequential damages</li>
                <li>Business losses, lost profits, or lost data are excluded</li>
                <li>Some jurisdictions may not allow these limitations</li>
              </ul>
            </div>
          </section>

          {/* Indemnification */}
          <section className="terms-section">
            <div className="section-header">
              <FaBalanceScale className="section-icon" />
              <h2>11. Indemnification</h2>
            </div>
            
            <div className="subsection">
              <p>You agree to indemnify and hold BroaderBusiness harmless from any claims, damages, or expenses arising from:</p>
              <ul>
                <li>Your use of the Service in violation of these Terms</li>
                <li>Content you create, upload, or distribute through our platform</li>
                <li>Your violation of any third-party rights or applicable laws</li>
                <li>Unauthorized use of your account due to your negligence</li>
                <li>Your business practices or marketing activities</li>
              </ul>
            </div>
          </section>

          {/* Termination */}
          <section className="terms-section">
            <div className="section-header">
              <FaExclamationTriangle className="section-icon" />
              <h2>12. Termination</h2>
            </div>
            
            <div className="subsection">
              <h3>12.1 Termination by You</h3>
              <ul>
                <li>You may terminate your account at any time through your account settings</li>
                <li>Cancellation takes effect at the end of your current billing period</li>
                <li>You remain responsible for any outstanding charges</li>
                <li>Data export tools are available before termination</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>12.2 Termination by Us</h3>
              <p>We may terminate your account for:</p>
              <ul>
                <li>Violation of these Terms or our policies</li>
                <li>Non-payment of fees</li>
                <li>Fraudulent or abusive behavior</li>
                <li>Legal requirements or compliance issues</li>
                <li>Extended period of inactivity (with notice)</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>12.3 Effect of Termination</h3>
              <ul>
                <li>Access to the Service immediately ceases</li>
                <li>Data is retained for 30 days for potential recovery</li>
                <li>Outstanding payment obligations continue</li>
                <li>Certain provisions of these Terms survive termination</li>
              </ul>
            </div>
          </section>

          {/* Dispute Resolution */}
          <section className="terms-section">
            <div className="section-header">
              <FaGavel className="section-icon" />
              <h2>13. Dispute Resolution</h2>
            </div>
            
            <div className="subsection">
              <h3>13.1 Governing Law</h3>
              <p>These Terms are governed by the laws of the State of California, without regard to conflict of law principles.</p>
            </div>

            <div className="subsection">
              <h3>13.2 Dispute Resolution Process</h3>
              <ol>
                <li><strong>Direct Communication:</strong> Contact our support team to resolve issues informally</li>
                <li><strong>Mediation:</strong> If direct resolution fails, we encourage mediation</li>
                <li><strong>Arbitration:</strong> Binding arbitration through the American Arbitration Association</li>
                <li><strong>Class Action Waiver:</strong> Both parties waive the right to class action lawsuits</li>
              </ol>
            </div>

            <div className="subsection">
              <h3>13.3 Exceptions</h3>
              <p>Either party may seek injunctive relief in court for:</p>
              <ul>
                <li>Intellectual property violations</li>
                <li>Confidentiality breaches</li>
                <li>Unauthorized access or security breaches</li>
              </ul>
            </div>
          </section>

          {/* General Provisions */}
          <section className="terms-section">
            <div className="section-header">
              <FaHandshake className="section-icon" />
              <h2>14. General Provisions</h2>
            </div>
            
            <div className="subsection">
              <h3>14.1 Entire Agreement</h3>
              <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and BroaderBusiness.</p>
            </div>

            <div className="subsection">
              <h3>14.2 Severability</h3>
              <p>If any provision is found unenforceable, the remaining provisions remain in full effect.</p>
            </div>

            <div className="subsection">
              <h3>14.3 Assignment</h3>
              <p>You may not assign these Terms without our consent. We may assign our rights and obligations freely.</p>
            </div>

            <div className="subsection">
              <h3>14.4 Waiver</h3>
              <p>Our failure to enforce any right or provision does not constitute a waiver of that right.</p>
            </div>

            <div className="subsection">
              <h3>14.5 Updates to Terms</h3>
              <ul>
                <li>We may update these Terms periodically</li>
                <li>Material changes require 30 days notice</li>
                <li>Continued use constitutes acceptance of updated Terms</li>
                <li>You may terminate if you disagree with changes</li>
              </ul>
            </div>
          </section>

          {/* Contact Information */}
          <section className="terms-section contact-section">
            <div className="section-header">
              <FaHandshake className="section-icon" />
              <h2>15. Contact Information</h2>
            </div>
            
            <div className="contact-info">
              <p>For questions about these Terms of Service, please contact us:</p>
              
              <div className="contact-methods">
                <div className="contact-item">
                  <strong>Email:</strong> legal@broaderbusiness.com
                </div>
                <div className="contact-item">
                  <strong>Support:</strong> support@broaderbusiness.com
                </div>
                <div className="contact-item">
                  <strong>Mail:</strong> BroaderBusiness Legal Department<br />
                  123 Business Street<br />
                  San Francisco, CA 94105
                </div>
                <div className="contact-item">
                  <strong>Phone:</strong> (555) 123-4567
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Agreement Footer */}
        <div className="terms-footer">
          <div className="agreement-summary">
            <h3>By using BroaderBusiness, you agree to:</h3>
            <div className="agreement-grid">
              <div className="agreement-item">
                <FaUserCheck className="agreement-icon" />
                <span>Use the service responsibly and legally</span>
              </div>
              <div className="agreement-item">
                <FaCreditCard className="agreement-icon" />
                <span>Pay all applicable fees on time</span>
              </div>
              <div className="agreement-item">
                <FaHandshake className="agreement-icon" />
                <span>Respect intellectual property rights</span>
              </div>
              <div className="agreement-item">
                <FaBalanceScale className="agreement-icon" />
                <span>Follow these Terms and our policies</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;